import { INVALID_SPANID, INVALID_TRACEID, context as otContext, trace as otTrace } from '@opentelemetry/api'
import { z } from 'zod'

export const CloudEventTraceSchema = z.object({
  traceId: z.string(),
  spanId: z.string(),
  parentSpanId: z.string().optional(),
})

export type CloudEventTrace = z.infer<typeof CloudEventTraceSchema>

/**
 * Read the current OpenTelemetry span context and return CloudEventTrace-compatible ids.
 * Returns undefined if no active span exists.
 */
export function getActiveOtelTrace(): CloudEventTrace | undefined {
  try {
    const span = otTrace.getActiveSpan()
    const ctx = span?.spanContext()
    if (!ctx) return undefined
    return { traceId: ctx.traceId, spanId: ctx.spanId }
  } catch {
    return undefined
  }
}

/**
 * Create an OpenTelemetry parent context from a CloudEventTrace for use when starting spans
 * in consumers that process events.
 */
export function makeOtelParentContext(trace?: CloudEventTrace) {
  // Guard if API surface isn't available or invalid input
  if (trace?.spanId && trace.spanId !== INVALID_SPANID && trace.traceId && trace.traceId !== INVALID_TRACEID) {
    const spanCtx = {
      traceId: trace.traceId,
      spanId: trace.spanId,
      traceFlags: 1,
      isRemote: true,
    } as const
    const nonRecording = otTrace.wrapSpanContext(spanCtx)
    return otTrace.setSpan(otContext.active(), nonRecording)
  } else {
    return otContext.active()
  }
}
